@use 'src/globals/main';

pn-action-menu {
  --pn-menu-height: unset;
  display: inline-block;
  position: relative;
}

pn-action-menu .pn-action-menu {
  position: relative;

  &-container {
    --pn-action-menu-offset: 0;

    scroll-behavior: smooth;
    list-style: none;
    text-align: left;

    position: absolute;
    z-index: 100;

    top: calc(100% + 0.5em);
    left: var(--pn-action-menu-offset);
    right: 0;

    background-color: main.$white;
    box-shadow:
      0em 0.075em 0.225em 0em rgba(0, 0, 0, 0.1),
      0em 0.4em 0.9em 0em rgba(0, 0, 0, 0.13);

    border-radius: main.$border-radius;
    margin: 0;
    padding: 0;
    width: 16em;

    visibility: hidden;
    overflow: hidden;

    &[data-open] {
      visibility: visible;
      overflow: unset;
    }

    &[data-left] {
      right: 0;
      left: unset;
    }

    &[data-upwards] {
      top: unset;
      bottom: calc(100% + 0.5em);
      transform-origin: bottom left;
    }

    &[data-moving] {
      overflow: hidden;
      visibility: visible;
      pointer-events: none;
    }

    /** Special rules for the small menu */
    &[data-small] {
      .pn-action-menu-list {
        border-radius: 0.5em;
        visibility: hidden;
        @include main.pn-transition('visibility');
        transition-delay: main.$timing;
        &[data-current] {
          overflow-x: auto;
          visibility: visible;
          transition-delay: 0s;
        }
      }

      &[data-moving] > .pn-action-menu-list {
        visibility: visible;
      }

      .pn-action-menu-item {
        position: unset;
      }

      .pn-action-menu-sub {
        left: 0;
        height: 100%;
        box-shadow: unset;
        transform: translateX(0);
        visibility: hidden;
        &[data-current] {
          visibility: visible;
          overflow-x: auto;
        }
      }
    }

    @include main.pn-scrollbar();
  }

  &-list {
    margin: 0;
    padding: 0;
    list-style: none;
    height: var(--pn-menu-height);
    @include main.pn-scrollbar();
  }

  &-group {
    margin: 0;
    padding: 0;
    list-style: none;
    &-label {
      display: block;
      color: main.$gray900;
      font-weight: 700;
      font-size: 0.875em;
      padding: 1em 1em 0.5em;
    }
    &-helpertext {
      display: block;
      color: main.$gray700;
      font-weight: 400;
      margin: 0.25em 0 0;
    }
  }

  &-sub {
    z-index: 110;
    position: absolute;
    top: 0;

    width: 100%;
    min-width: 12em;

    margin: 0;
    padding: 0;

    list-style: none;
    background: main.$white;
    border-radius: main.$border-radius;
    box-shadow:
      0em 0.075em 0.225em 0em rgba(0, 0, 0, 0.1),
      0em 0.4em 0.9em 0em rgba(0, 0, 0, 0.13);

    transform: translateX(-0.5em);
    opacity: 0;
    visibility: hidden;

    @include main.pn-scrollbar();
    @include main.pn-transition('transform, opacity, visibility');
    transition-delay: 0s, 0s, main.$timing;

    &[data-open='true'] {
      transform: translateX(0);
      opacity: 1;
      visibility: visible;
      transition-delay: 0s, 0s, 0s;
    }

    &[data-open='false'] {
      pointer-events: none;
    }

    &[data-x='left'] {
      left: -100%;
    }

    &[data-x='center'] {
      left: 0;
    }

    &[data-x='right'] {
      left: 100%;
    }

    &[data-y='top'] {
      top: unset;
      bottom: 0;
    }

    &[data-y='bottom'] {
      top: 0;
    }
  }

  &-item {
    position: relative;

    &:first-child > .pn-action-menu-item-content > .pn-action-menu-button {
      border-top-left-radius: 0.5em;
      border-top-right-radius: 0.5em;
    }
    &:last-child > .pn-action-menu-item-content > .pn-action-menu-button {
      border-bottom-right-radius: 0.5em;
      border-bottom-left-radius: 0.5em;
    }

    &-text {
      display: flex;
      flex-direction: column;
      margin-right: auto;
    }

    &-label {
      color: main.$blue700;
      font-weight: 500;
      text-decoration: none;
    }

    &-suffix {
      margin-left: auto;
      font-weight: 400;
      color: main.$gray700;
    }

    &-helpertext {
      color: main.$gray700;
      font-weight: 400;
    }

    &-content {
      display: flex;
      align-items: center;
      flex-direction: row;
      flex: 1 1 100%;
      gap: 0.5em;
      position: relative;

      &[data-close] {
        border-bottom: 0.0625em solid main.$gray200;
      }

      > pn-icon {
        padding: 0 0.25em;
      }
    }

    &[data-group],
    &[data-sub] {
      flex-direction: column;
    }
  }

  &-button {
    cursor: pointer;
    position: relative;
    -webkit-tap-highlight-color: transparent;

    line-height: 1.5;
    padding: 0.75em 1em;
    border: 0;
    width: 100%;
    text-align: left;
    text-decoration-color: transparent;

    overflow: hidden;

    display: flex;
    align-items: center;
    flex: 1 1 100%;
    gap: 0.5em;

    font-size: 1em;
    min-height: 1.5em;
    background-color: main.$white;

    @include main.pn-transition('background-color, color, outline-color, text-decoration-color');
    @include main.pn-ripple(main.$blue900);
    @include main.pn-focus();
    outline-offset: -0.2rem;

    > pn-icon {
      .pn-icon-svg > path {
        transform-origin: center;
        transform: rotate(0deg);
        @include main.pn-transition('transform');
      }

      &[data-suffix][data-active] > .pn-icon-svg > path {
        transform: rotate(180deg);
      }

      &:last-child {
        margin-left: auto;
      }
    }

    &:disabled {
      pointer-events: none;
      color: main.$gray700;
      background-color: main.$gray50;
    }

    &:hover {
      background-color: main.$blue25;
    }

    &:focus {
      background-color: main.$blue50;
    }

    &[href] {
      &:hover,
      &:focus {
        text-decoration-color: main.$blue700;
      }
    }

    /*
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      width: 0.25em;
      height: 100%;
      background-color: transparent;
      @include main.pn-transition('background-color');
    }

    &[aria-expanded='true']::before {
      background-color: main.$blue700;
    }
    */
  }

  &-checkbox {
    background-color: main.$white;
    flex: 0 0 1.5em;
    width: 1.5em;
    height: 1.5em;
    border: 0.0625em solid main.$gray400;
    border-radius: 0.25em;
    margin-left: auto;

    @include main.pn-focus-base;
    @include main.pn-transition('background-color, border-color, outline-color');

    svg polyline {
      stroke: main.$white;
      stroke-linecap: round;
      stroke-dasharray: 23;
      @include main.pn-transition('stroke-dashoffset');
      transition-delay: 0s;

      &.pn-action-menu-checkbox-checkmark-path {
        stroke-dashoffset: 23;
      }
    }
  }

  &-radio {
    display: flex;
    gap: 0.75em;
    padding: 0;
    margin-left: auto;

    &-outer {
      background-color: main.$input-background;
      border: main.$input-border;
      border-radius: 50%;
      height: 1.5em;
      width: 1.5em;
      display: flex;
      justify-content: center;
      align-items: center;

      @include main.pn-focus-base;
      @include main.pn-transition('border-color, background-color, outline-color');
    }

    &-inner {
      transform: scale(0);
      height: 0.75em;
      width: 0.75em;
      background-color: main.$input-button-background-box-active;
      border-radius: 50%;
      transform-origin: center center;
      @include main.pn-transition('background-color, transform');
    }
  }

  &-input {
    position: absolute;
    z-index: 10;
    cursor: pointer;
    height: 100%;
    width: 100%;
    top: 0;
    left: 0;
    margin: 0;
    opacity: 0;
    -webkit-tap-highlight-color: transparent;

    &:checked {
      + .pn-action-menu-button {
        background-color: main.$blue25;
        .pn-action-menu-checkbox {
          border-color: main.$blue700;
          background-color: main.$blue700;
          polyline.pn-action-menu-checkbox-checkmark-path {
            transition-delay: main.$timing;
            stroke-dashoffset: 0;
          }
        }
        .pn-action-menu-radio {
          &-outer {
            border-color: main.$border-color-default-active;
          }
          &-inner {
            transform: scale(1);
            background-color: main.$input-button-background-box-active;
          }
        }
      }

      &:hover {
        background-color: main.$blue50;
      }
    }

    &:focus-visible + .pn-action-menu-button {
      background-color: main.$blue50;
    }

    &:disabled {
      pointer-events: none;
      + .pn-action-menu-button {
        pointer-events: none;
        color: main.$gray700;
        background-color: main.$gray50;

        .pn-action-menu-item-label {
          color: main.$gray700;
        }
        .pn-action-menu-checkbox {
          background-color: main.$gray50;
        }
        .pn-action-menu-radio-outer {
          border-color: main.$gray400;
          background-color: main.$gray50;
        }
        .pn-icon-svg path {
          fill: main.$gray700;
        }
      }

      &:checked + .pn-action-menu-button {
        .pn-action-menu-radio-inner {
          background-color: main.$gray400;
        }
        .pn-action-menu-checkbox {
          border-color: main.$gray400;
          background-color: main.$gray400;
        }
      }
    }

    &:hover + .pn-action-menu-button {
      background-color: main.$blue50;
      .pn-action-menu-radio-outer {
        border-color: main.$input-button-background-box-active;
        background-color: main.$input-button-background-hover;
      }
    }

    &:focus-visible + .pn-action-menu-button {
      background-color: main.$blue50;
      .pn-action-menu-checkbox,
      .pn-action-menu-radio .pn-action-menu-radio-outer {
        @include main.pn-focus-visible();
      }
    }
  }

  &-p {
    margin: 0;
  }

  > pn-button[data-default-icon] .pn-button {
    &[aria-expanded='true'] .pn-icon-svg {
      transform: rotate(180deg);
      transition-delay: 0s;
    }
    .pn-icon-svg {
      transform: rotate(0deg);
      transition-delay: main.$timing;
      @include main.pn-transition('transform');
    }
  }
}
